// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { CreateDealSteps, CustomerFormGroup, DealerFormGroup, DealTypeInputs } from '@interfaces/create-deal-inputs.interface';
import { TradeInVehicle, VehicleCreation } from '@interfaces/vehicle.interfaces';
import { catchError, takeWhile } from 'rxjs/operators';
import { DealsService } from '@services/deals.service';
import { phoneExporter } from 'src/app/utils/form-utils';
import { Assignees, TemplateGroup } from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { UserService } from '@services/user.service';
import { Deal } from '@interfaces/deal.interfaces';
import { DealCreatedModalComponent } from 'src/app/modals/deal-created-modal/deal-created-modal.component';
import { UserTypes } from '@interfaces/user.interfaces';

@Component({
    selector: 'app-create-deal',
    templateUrl: './create-deal.component.html',
    styles: [
        `
            .add-cobuyer-button {
                display: flex;
                justify-content: flex-end;
            }
            .template-selection-container {
                display: flex;
                justify-content: space-around;
            }
            .buyer-found,
            .cobuyer-found,
            .customer-error {
                color: red;
            }
            .trade-in-card .card-header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 0;
            }
            .trade-in-card .card-header h2 {
                margin: 10px 0;
            }
        `,
    ],
    providers: [MatStepper],
})
export class CreateDealComponent implements OnInit, OnDestroy {
    @ViewChild('stepper', { static: true }) private stepper: MatStepper;
    @ViewChild('step1', { static: false }) private step1: MatStep;
    @ViewChild('step2', { static: false }) private step2: MatStep;
    @ViewChild('step3', { static: false }) private step3: MatStep;
    addCoBuyer = false;
    buyerFound = false;
    coBuyerLogonId: string;
    customerLogonId: string;
    coBuyerFound = false;
    createDealForm = this.fb.group({});
    createDealSteps = CreateDealSteps;
    customerFormProperties: CustomerFormGroup;
    coBuyerFormProperties: CustomerFormGroup;
    dealerFormProperties: DealerFormGroup;
    dealTypeSelections: DealTypeInputs = {
        isNew: true,
        isBuy: true,
        isCash: true,
        isTradeIn: false,
        isLeaseTurnIn: false,
    };
    groups: TemplateGroup[] = [];
    isReadyCoBuyer = false;
    isReadyCustomer = false;
    isReadyVehicle = false;
    isBuyerDealer = false;
    isCobuyerDealer = false;
    selectedVehicle: VehicleCreation = null;
    tradeInVehicle: TradeInVehicle;
    isComponentAlive = true;
    isLoading = false;
    isManuallyCreated = false;
    hasTradeInVehicle = false;
    templateGroupSelection: TemplateGroup;
    templateSelectionType: string = null;
    triggerCustomerFormErrors = false;
    triggerCoBuyerFormErrors = false;
    duplicateBuyer = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly dealsService: DealsService,
        private readonly dialog: MatDialog,
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly userService: UserService
    ) {}

    async ngOnInit(): Promise<void> {
        this.isComponentAlive = true;

        this.stepper.selectionChange.pipe(takeWhile(() => this.isComponentAlive)).subscribe((evt) => {
            switch (evt.selectedIndex) {
                case CreateDealSteps.dealType:
                    this.step1.completed = false;
                    this.step2.completed = false;
                    this.step3.completed = false;
                    break;
                case CreateDealSteps.details:
                    this.step2.completed = false;
                    this.step3.completed = false;
            }
        });

        this.route.data.pipe(takeWhile(() => this.isComponentAlive)).subscribe((resolve: any) => {
            this.groups = resolve.templateGroups;

            if (!this.groups.length) {
                this.templateSelectionType = 'dealType';
            }
        });
    }

    addCoBuyerToDeal(): void {
        this.addCoBuyer = !this.addCoBuyer;
        this.coBuyerFound = false;
        this.duplicateBuyer = false;

        if (!this.addCoBuyer) {
            this.coBuyerFormProperties = null;
            this.duplicateBuyer = false;
            return;
        }

        !this.coBuyerFormProperties || this.duplicateBuyer ? (this.isReadyCoBuyer = false) : (this.isReadyCoBuyer = true);
    }

    attachBuyerForm(customerForm: any) {
        if (customerForm.phone && !customerForm.phone.includes('*')) {
            let cleansedPhone = customerForm.phone.replace(/-/g, '');
            cleansedPhone = cleansedPhone.substring(0, 10);
            customerForm.phone = cleansedPhone;
        }

        if (customerForm.phone && customerForm.phone.includes('*')) {
            customerForm.phone = this.customerFormProperties.phone;
        }

        this.customerFormProperties = customerForm;

        this.checkFormValues(this.customerFormProperties) ? (this.isReadyCustomer = false) : (this.isReadyCustomer = true);
    }

    customerLogon(logonId: any) {
        this.customerLogonId = logonId;
    }

    coBuyerLogon(logonId: any) {
        this.coBuyerLogonId = logonId;
    }

    attachCoBuyerForm(customerForm: any) {
        if (customerForm.phone && !customerForm.phone.includes('*')) {
            let cleansedPhone = customerForm.phone.replace(/-/g, '');
            cleansedPhone = cleansedPhone.substring(0, 10);
            customerForm.phone = cleansedPhone;
        }

        if (customerForm.phone && customerForm.phone.includes('*')) {
            customerForm.phone = this.coBuyerFormProperties.phone;
        }

        this.coBuyerFormProperties = customerForm;

        if (this.addCoBuyer) {
            this.checkFormValues(this.coBuyerFormProperties) || this.duplicateBuyer ? (this.isReadyCoBuyer = false) : (this.isReadyCoBuyer = true);
        }
    }

    toggleTradeInVehicle() {
        this.hasTradeInVehicle = !this.hasTradeInVehicle;
    }

    updateTradeInVehicle(event: any) {
        this.tradeInVehicle = {
            bodyStyle: event.bodyStyle,
            bodyType: event.bodyType,
            make: event.make,
            model: event.model,
            stockNumber: event.stockNumber,
            trim: event.trim,
            vehicleExt: {
                bodyType: event.bodyType,
                exteriorColor: event.exteriorColor,
                interiorColor: event.interiorColor,
                priceInternet: event.price,
            },
            vin: event.vin,
            year: event.year,
            payoff: event.payoff,
        } as TradeInVehicle;
    }

    disableNext(disableNext: any, assigneeType: any) {
        if (assigneeType === Assignees.buyer) {
            this.isReadyCustomer = !this.checkFormValues(this.customerFormProperties);
        } else {
            this.isReadyCoBuyer = !this.checkFormValues(this.coBuyerFormProperties) || this.duplicateBuyer;
        }
    }

    isUserFound(isUserFound: any, assigneeType: any) {
        if (assigneeType === Assignees.buyer) {
            this.buyerFound = isUserFound !== false;
            this.isBuyerDealer = isUserFound && isUserFound.userType !== UserTypes.Customer;
        }

        if (assigneeType === Assignees.cobuyer) {
            this.coBuyerFound = isUserFound !== false;
            this.isCobuyerDealer = isUserFound && isUserFound.userType !== UserTypes.Customer;
        }

        if (this.coBuyerFormProperties?.email && this.customerFormProperties?.email) {
            if (this.customerFormProperties.email.toLowerCase().trim() === this.coBuyerFormProperties.email.toLowerCase().trim()) {
                this.duplicateBuyer = true;
                this.isReadyCoBuyer = false;
            } else {
                this.duplicateBuyer = false;
                this.isReadyCoBuyer = true;
            }
        } else {
            this.duplicateBuyer = false;
        }
    }

    attachDealerForm(dealerForm: any) {
        this.dealerFormProperties = dealerForm;
    }

    isControlLoading(isLoading: any) {
        this.isLoading = isLoading;
    }

    checkFormValues(form: CustomerFormGroup): boolean {
        return Object.values(form).some((x) => x === null || x === '') ? true : false;
    }

    createDealSubmit() {
        this.isLoading = true;
        this.customerFormProperties.phone = phoneExporter(this.customerFormProperties.phone);
        this.customerFormProperties.logonId = this.customerLogonId;

        if (this.coBuyerFormProperties) {
            this.coBuyerFormProperties.phone = phoneExporter(this.coBuyerFormProperties.phone);
            this.coBuyerFormProperties.logonId = this.coBuyerLogonId;
        }

        let customTemplateGroupDeal = null;

        if (this.templateGroupSelection) {
            customTemplateGroupDeal = {
                templateGroupId: this.templateGroupSelection.templateGroupId,
                dealerId: this.userService.getDealerId(),
            };

            Object.keys(this.dealTypeSelections).forEach((key) => {
                this.dealTypeSelections[key] = false;
            });
        }
        this.dealsService
            .createDeal$(
                this.dealTypeSelections,
                this.customerFormProperties,
                this.selectedVehicle,
                this.userService.getDealerId(),
                this.coBuyerFormProperties,
                this.dealerFormProperties,
                customTemplateGroupDeal,
                this.tradeInVehicle,
                null
            )
            .pipe(
                catchError((error) => {
                    this.isLoading = false;
                    return error;
                }),
                takeWhile(() => this.isComponentAlive)
            )
            .subscribe((deal: Deal) => {
                this.isLoading = false;
                this.router.navigate(['/deals', 'deal', deal.dealId]);
                this.dialog.open(DealCreatedModalComponent, {
                    width: '700px',
                    closeOnNavigation: false,
                    data: deal.dealId,
                });
            });
    }

    dealDetailsFormSubmit() {
        this.step2.completed = true;

        if (!this.hasTradeInVehicle) {
            this.tradeInVehicle = undefined;
        }

        this.goToStep(CreateDealSteps.summary);
    }

    dealTypeFormSubmit() {
        this.step1.completed = true;
        this.goToStep(CreateDealSteps.details);
    }

    eventHandler(event: AppEvent): void {
        this.templateGroupSelection = event.data;
    }

    goToStep(step: CreateDealSteps) {
        this.stepper.selectedIndex = step;
    }

    selectVehicle(vehicle: VehicleCreation) {
        if (vehicle) {
            this.isReadyVehicle = true;
            this.selectedVehicle = vehicle;

            if (this.addCoBuyer) {
                if (!this.customerFormProperties && !this.coBuyerFormProperties) {
                    this.triggerCustomerFormErrors = true;
                    this.triggerCoBuyerFormErrors = true;
                    return;
                }

                if (!this.customerFormProperties || this.checkFormValues(this.customerFormProperties)) {
                    this.triggerCustomerFormErrors = true;
                }

                if (!this.coBuyerFormProperties || this.checkFormValues(this.coBuyerFormProperties)) {
                    this.triggerCoBuyerFormErrors = true;
                }
            }

            if (!this.customerFormProperties) {
                this.triggerCustomerFormErrors = true;
                return;
            }

            if (this.checkFormValues(this.customerFormProperties)) {
                this.triggerCustomerFormErrors = true;
                return;
            }
        } else {
            this.isReadyVehicle = false;
            this.selectedVehicle = null;
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
